import { z } from 'zod';
import { LEAD_SOURCES, LEAD_STAGES } from '../enums.js';
import {
  AddressSchema, CommsPrefsSchema, ContactSchema, CustomerTypeSchema, EntityMetaSchema,
  zEmail, zEnum, zNonEmpty, zPhone, zTag,
} from './common.js';

/** A service location belonging to a customer (a property where work happens). */
export const ServiceLocationSchema = z.object({
  id: z.string(),
  label: z.string().trim().max(80).optional(), // e.g. "Main office", "Rental #2"
  address: AddressSchema,
  notes: z.string().max(2000).optional(),
});
export type ServiceLocation = z.infer<typeof ServiceLocationSchema>;

/** Equipment/asset tracked at a customer/location. */
export const EquipmentSchema = EntityMetaSchema.extend({
  customerId: z.string(),
  locationId: z.string().optional(),
  type: zNonEmpty.max(80), // e.g. "AC Condenser", "Water Heater"
  manufacturer: z.string().max(80).optional(),
  model: z.string().max(80).optional(),
  serialNumber: z.string().max(120).optional(),
  installDate: z.string().optional(),
  warrantyExpires: z.string().optional(),
  notes: z.string().max(2000).optional(),
  photoKeys: z.array(z.string()).default([]),
});
export type Equipment = z.infer<typeof EquipmentSchema>;

export const CreateEquipmentSchema = z.object({
  customerId: zNonEmpty,
  propertyId: z.string().optional(),
  type: zNonEmpty.max(80),
  manufacturer: z.string().max(80).optional(),
  model: z.string().max(80).optional(),
  serialNumber: z.string().max(120).optional(),
  installDate: z.string().optional(),
  warrantyExpires: z.string().optional(),
  notes: z.string().max(2000).optional(),
});
export type CreateEquipmentInput = z.infer<typeof CreateEquipmentSchema>;

export const CustomerSchema = EntityMetaSchema.extend({
  type: CustomerTypeSchema.default('residential'),
  displayName: zNonEmpty.max(160), // person name or company name
  companyName: z.string().max(160).optional(),
  contacts: z.array(ContactSchema).default([]),
  emails: z.array(zEmail).default([]),
  phones: z.array(zPhone).default([]),
  billingAddress: AddressSchema.optional(),
  serviceLocations: z.array(ServiceLocationSchema).default([]),
  tags: z.array(zTag).default([]),
  source: zEnum(LEAD_SOURCES).optional(),
  ownerUserId: z.string().optional(),
  commsPrefs: CommsPrefsSchema.default({}),
  notes: z.string().max(8000).optional(),
  customFields: z.record(z.string(), z.string()).default({}),
  // Server-maintained rollups (read-only to clients):
  lifetimeValueCents: z.number().int().default(0),
  outstandingBalanceCents: z.number().int().default(0),
});
export type Customer = z.infer<typeof CustomerSchema>;

/** Client-writable subset for create/update (server owns meta + rollups). */
export const CreateCustomerSchema = z.object({
  type: CustomerTypeSchema.optional(),
  displayName: zNonEmpty.max(160),
  companyName: z.string().max(160).optional(),
  contacts: z.array(ContactSchema).optional(),
  emails: z.array(zEmail).optional(),
  phones: z.array(zPhone).optional(),
  billingAddress: AddressSchema.optional(),
  serviceLocations: z.array(ServiceLocationSchema.omit({ id: true })).optional(),
  tags: z.array(zTag).optional(),
  source: zEnum(LEAD_SOURCES).optional(),
  ownerUserId: z.string().optional(),
  commsPrefs: CommsPrefsSchema.partial().optional(),
  notes: z.string().max(8000).optional(),
  customFields: z.record(z.string(), z.string()).optional(),
});
export type CreateCustomerInput = z.infer<typeof CreateCustomerSchema>;
export const UpdateCustomerSchema = CreateCustomerSchema.partial();
export type UpdateCustomerInput = z.infer<typeof UpdateCustomerSchema>;

/** Lead (tenant lead = a homeowner/business who might hire this tenant). */
export const LeadSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(160),
  email: zEmail.optional(),
  phone: zPhone.optional(),
  address: AddressSchema.optional(),
  source: zEnum(LEAD_SOURCES).default('manual'),
  stage: zEnum(LEAD_STAGES).default('new'),
  ownerUserId: z.string().optional(),
  serviceNeeded: z.string().max(2000).optional(),
  estimatedValueCents: z.number().int().min(0).optional(),
  followUpDate: z.string().optional(), // ISO date
  notes: z.string().max(8000).optional(),
  convertedCustomerId: z.string().optional(),
  lostReason: z.string().max(500).optional(),
  customFields: z.record(z.string(), z.string()).default({}),
  // Marketing attribution (Parts 31-33), preserved through the lifecycle.
  utmSource: z.string().max(120).optional(),
  utmMedium: z.string().max(120).optional(),
  utmCampaign: z.string().max(120).optional(),
  landingPage: z.string().max(200).optional(),
  referrer: z.string().max(200).optional(),
});
export type Lead = z.infer<typeof LeadSchema>;

export const CreateLeadSchema = z.object({
  name: zNonEmpty.max(160),
  email: zEmail.optional(),
  phone: zPhone.optional(),
  address: AddressSchema.optional(),
  source: zEnum(LEAD_SOURCES).optional(),
  stage: zEnum(LEAD_STAGES).optional(),
  ownerUserId: z.string().optional(),
  serviceNeeded: z.string().max(2000).optional(),
  estimatedValueCents: z.number().int().min(0).optional(),
  followUpDate: z.string().optional(),
  notes: z.string().max(8000).optional(),
}).refine((v) => v.email || v.phone, {
  message: 'Provide at least an email or phone number',
  path: ['phone'],
});
export type CreateLeadInput = z.infer<typeof CreateLeadSchema>;

export const UpdateLeadSchema = z.object({
  name: zNonEmpty.max(160).optional(),
  email: zEmail.optional(),
  phone: zPhone.optional(),
  address: AddressSchema.optional(),
  source: zEnum(LEAD_SOURCES).optional(),
  stage: zEnum(LEAD_STAGES).optional(),
  ownerUserId: z.string().optional(),
  serviceNeeded: z.string().max(2000).optional(),
  estimatedValueCents: z.number().int().min(0).optional(),
  followUpDate: z.string().optional(),
  notes: z.string().max(8000).optional(),
  lostReason: z.string().max(500).optional(),
});
export type UpdateLeadInput = z.infer<typeof UpdateLeadSchema>;
